import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import useAuthStore from '../store/authStore';
import useSidebarStore from '../store/sidebarStore';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useRecentsStore, { forCenter } from '../store/recentsStore';
import useNavMenuItems from '../config/navMenu';

export default function Sidebar() {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, centerType } = useAuthStore();
  const { collapsed } = useSidebarStore();
  // Phase 3.2 — recently visited students + groups
  const activeCenterId = useAuthStore((s) => s.centerId);
  const recentStudents = forCenter(useRecentsStore((s) => s.students), activeCenterId);
  const recentGroups = forCenter(useRecentsStore((s) => s.groups), activeCenterId);

  // When collapsed, hovering temporarily expands
  const isEffectivelyCollapsed = collapsed && !isHovered;

  const menuItems = useNavMenuItems('desktop');
  const navLabel = (item) =>
    t(item.labelKey, item.labelFallback ? { defaultValue: item.labelFallback } : undefined);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarWidth = isEffectivelyCollapsed ? '4.5rem' : '16rem';

  return (
    <>
      <style>{`
        @keyframes sidebarSlideIn {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .sidebar-open {
          animation: sidebarSlideIn 0.3s ease;
        }

        .sidebar-overlay {
          animation: fadeIn 0.2s ease;
        }
      `}</style>

      {/* Mobile toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
        style={{
          position: 'fixed',
          top: '1.5rem',
          right: '1.5rem',
          zIndex: 50,
          padding: '0.75rem',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-primary)',
          border: 'none',
          borderRadius: '0.75rem',
          cursor: 'pointer',
          display: window.innerWidth > 768 ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        }}
      >
        {isOpen ? <Icon name="close" size="lg" color="white" /> : <Icon name="menu" size="lg" color="white" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 40,
            display: window.innerWidth > 768 ? 'none' : 'block',
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={isOpen ? 'sidebar-open' : ''}
        onMouseEnter={() => { if (collapsed) setIsHovered(true); }}
        onMouseLeave={() => { if (collapsed) setIsHovered(false); }}
        style={{
          position: 'fixed',
          [isRTL ? 'right' : 'left']: 0,
          top: '64px',
          height: 'calc(100vh - 64px)',
          background: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
          width: sidebarWidth,
          transform: window.innerWidth > 768 ? 'translateX(0)' : (isOpen ? 'translateX(0)' : (isRTL ? 'translateX(100%)' : 'translateX(-100%)')),
          transition: 'transform 0.3s ease, width 0.3s ease',
          zIndex: 40,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: collapsed && isHovered ? '0 4px 20px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
          borderRight: !isRTL ? '1px solid var(--color-border)' : 'none',
          borderLeft: isRTL ? '1px solid var(--color-border)' : 'none',
          direction: isRTL ? 'rtl' : 'ltr',
          overflow: 'hidden',
        }}>

        {/* Navigation */}
        <nav style={{
          padding: isEffectivelyCollapsed ? '1rem 0.5rem' : '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            // Map paths to data-tour attributes
            const dataTourMap = {
              '/students': 'students-nav',
              '/teachers': 'teachers-nav',
              '/groups': 'groups-nav',
              '/jibaya': 'jibaya-nav',
              '/settings': 'settings-nav'
            };
            return (
              <button
                key={item.path}
                data-tour={dataTourMap[item.path] || undefined}
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
                title={navLabel(item)}
                style={{
                  width: isEffectivelyCollapsed ? '3rem' : 'calc(100% - 1rem)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: isEffectivelyCollapsed ? '0.75rem' : '0.75rem 0.875rem',
                  margin: isEffectivelyCollapsed ? '0.25rem auto' : '0.25rem 0.5rem',
                  border: active ? '2px solid var(--color-primary)' : '2px solid transparent',
                  borderRadius: '0.625rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: active ? 'rgba(53, 87, 96, 0.08)' : 'transparent',
                  color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: active ? '600' : '500',
                  boxShadow: active ? '0 2px 8px rgba(53, 87, 96, 0.12)' : 'none',
                  justifyContent: isEffectivelyCollapsed ? 'center' : 'flex-start',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = 'rgba(53, 87, 96, 0.04)';
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.borderColor = 'rgba(53, 87, 96, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                    e.currentTarget.style.borderColor = 'transparent';
                  }
                }}
              >
                <Icon name={item.iconName} size="md" color={active ? 'primary' : 'text-secondary'} style={{ flexShrink: 0 }} />
                {!isEffectivelyCollapsed && (
                  // A label longer than the rail ends in "…" (full text in the
                  // tooltip) instead of being cut mid-word by the button.
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: isRTL ? 'right' : 'left' }}>{navLabel(item)}</span>
                )}
              </button>
            );
          })}

          {/* Phase 3.2 — recently visited entities. Hidden when sidebar collapsed
              (icon-rail mode) since the items wouldn't fit a 4.5rem column. */}
          {!isEffectivelyCollapsed && (recentStudents.length > 0 || recentGroups.length > 0) && (
            <div style={{
              marginTop: 'var(--space-3)',
              paddingTop: 'var(--space-3)',
              borderTop: '1px solid var(--border-light)',
            }}>
              {recentStudents.length > 0 && (
                <RecentBlock
                  title={t('sidebar.recentStudents', { defaultValue: 'Recent students' })}
                  items={recentStudents.slice(0, 3)}
                  pathFor={(it) => `${centerType === 'learning' ? '/students' : '/clients'}/${it.id}`}
                  onClick={(p) => { navigate(p); setIsOpen(false); }}
                  isRTL={isRTL}
                />
              )}
              {recentGroups.length > 0 && (
                <RecentBlock
                  title={t('sidebar.recentGroups', { defaultValue: 'Recent groups' })}
                  items={recentGroups.slice(0, 3)}
                  pathFor={(it) => `/groups/${it.id}`}
                  onClick={(p) => { navigate(p); setIsOpen(false); }}
                  isRTL={isRTL}
                />
              )}
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}

function RecentBlock({ title, items, pathFor, onClick, isRTL }) {
  return (
    <div style={{ padding: '0 var(--space-3)', marginBottom: 'var(--space-2)' }}>
      <div style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-tertiary)',
        padding: '0.375rem 0.5rem',
      }}>
        {title}
      </div>
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onClick(pathFor(it))}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isRTL ? 'flex-end' : 'flex-start',
            gap: 0,
            padding: '0.375rem 0.5rem',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            borderRadius: 'var(--radius-sm)',
            textAlign: isRTL ? 'right' : 'left',
            fontSize: 'var(--font-size-sm)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--neutral-100)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{
            color: 'var(--text-primary)',
            fontWeight: 500,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {it.label}
          </span>
          {it.sublabel && (
            <span style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-tertiary)',
            }}>
              {it.sublabel}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
